import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  FileSearch,
  PackageX,
  Printer,
  RefreshCw,
  Search,
  Tags,
  Trash2,
  Truck,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import { useSessao } from '@/hooks/useSessao';
import { useDebounce } from '@/hooks/useDebounce';
import { useParamUrl } from '@/hooks/useUrlEstado';
import { abrirRomaneioNoEdge } from '@/lib/imprimirPapel';
import { cn, numero } from '@/lib/utils';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { Segmentado } from '@/componentes/ui/segmentado';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import {
  CabecalhoPagina,
  Cartao,
  CorpoCartao,
  Esqueleto,
  Pilula,
} from '@/componentes/ui/primitivos';
import {
  CSS_ROMANEIO,
  RomaneioPapel,
  type LinhaRomaneio,
} from '@/componentes/romaneio/RomaneioPapel';

/**
 * Romaneios — a etapa depois de faturar.
 *
 * O romaneio é montado no COLETOR, bipando as etiquetas por transportadora.
 * Esta tela não monta nenhum: ela resolve o que vem depois e hoje obriga a
 * abrir o Procfit — achar o romaneio, ver o que foi dentro dele e reimprimir
 * o papel que o motorista assina.
 *
 * O gesto principal é digitar o número. Quem precisa reimprimir tem o número
 * na mão (está no papel rasgado, ou o motorista ditou), então o campo fica no
 * cabeçalho e abre direto — a grade dos últimos é para quando não se tem o
 * número, não o contrário.
 *
 * Empresa: a grade lista só romaneios que carregam pedido da empresa
 * selecionada, e o número digitado passa pela MESMA trava no servidor —
 * senão o campo de número seria um jeito de ler o romaneio de qualquer
 * empresa. A conta de "de quem é o romaneio" sai dos pedidos, não de
 * `TELEVENDAS_ROMANEIO.EMPRESA`, que o coletor deixa vazia.
 */

interface RomaneioResumo {
  romaneio: number;
  data: string | null;
  cracha: number | null;
  transportadora: number | null;
  transportadoraNome: string | null;
  pedidos: number;
  volumes: number;
  empresas: number[];
  observacao: string | null;
}

interface RomaneiosEmOutraEmpresa {
  empresa: number;
  nome: string | null;
  romaneios: number;
  ultimo: string | null;
}

interface RespostaLista {
  empresa: number;
  romaneios: RomaneioResumo[];
  /**
   * Onde MAIS a busca acharia romaneio — só vem quando a lista veio vazia.
   *
   * Transportadora atende várias empresas do grupo, então "não achei" quase
   * sempre quer dizer "não achei NESTA empresa". Ver o comentário gêmeo em
   * modules/romaneio/queries.ts.
   */
  outrasEmpresas?: RomaneiosEmOutraEmpresa[];
}

/**
 * Uma comanda do romaneio — o que está GRAVADO, saia no papel ou não.
 *
 * A tabela do papel (`linhas`) esconde nota cancelada, que é justamente o
 * pedido que se quer tirar da carga. `comandas` é a lista sem esse corte.
 */
interface ComandaRomaneio {
  televenda: number;
  empresa: number | null;
  notaFiscal: number | null;
  situacao: 'faturada' | 'cancelada' | 'sem_nota';
  cidade: string | null;
  uf: string | null;
  canceladaEm: string | null;
}

interface RomaneioDetalhe {
  cabecalho: RomaneioResumo;
  linhas: LinhaRomaneio[];
  comandas: ComandaRomaneio[];
  qtdePedidos: number;
  totalVolumes: number;
  totalPesoGramas: number;
}

const DIAS = [
  { valor: '1', rotulo: 'Hoje' },
  { valor: '7', rotulo: '7 dias' },
  { valor: '30', rotulo: '30 dias' },
] as const;

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

export function Romaneios(): JSX.Element {
  const { empresa } = useContexto();

  const [selecionado, definirSelecionado] = useParamUrl('romaneio');
  const [busca, definirBusca] = React.useState('');
  const buscaAtrasada = useDebounce(busca, 300);
  const [dias, definirDias] = React.useState<'1' | '7' | '30'>('7');

  const lista = useQuery<RespostaLista>({
    queryKey: ['romaneios', empresa, dias, buscaAtrasada],
    queryFn: () =>
      api.get<RespostaLista>('/api/romaneios', {
        empresa,
        dias,
        q: buscaAtrasada.trim() || undefined,
      }),
    enabled: empresa !== null,
    staleTime: 30_000,
  });

  const numeroSelecionado = Number(selecionado) || null;

  const detalhe = useQuery<RomaneioDetalhe>({
    queryKey: ['romaneio', numeroSelecionado],
    queryFn: () => api.get<RomaneioDetalhe>(`/api/romaneios/${numeroSelecionado}`),
    enabled: numeroSelecionado !== null,
  });

  return (
    <div className="space-y-4">
      <CabecalhoPagina
        sobrelinha="Operação"
        titulo="Romaneios"
        descricao="O romaneio é montado no coletor. Aqui você acha, confere o que foi dentro e reimprime."
        icone={<Truck className="h-5 w-5" />}
        acoes={
          <>
            <AbrirPorNumero aoAbrir={(n) => definirSelecionado(String(n))} />
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => void lista.refetch()}
              carregando={lista.isFetching}
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </Botao>
          </>
        }
      />

      {/*
        Duas colunas que rolam sozinhas — ver o comentário gêmeo em
        Etiquetas.tsx, que explica por que `sticky` não servia aqui (o scroll
        é do `<main>`, não do documento).
      */}
      <div className="grid items-start gap-3 lg:h-[calc(100vh-190px)] lg:min-h-[420px] lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-stretch">
        <Cartao className="nao-imprimir flex flex-col lg:min-h-0 lg:overflow-hidden">
          <CorpoCartao className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            <div className="flex flex-wrap items-center gap-2">
              <Segmentado
                tamanho="pequeno"
                valor={dias}
                aoMudar={definirDias}
                rotuloAcessivel="Período da lista"
                opcoes={DIAS.map((d) => ({ valor: d.valor, rotulo: d.rotulo }))}
              />
              {lista.data ? (
                <Pilula tom="neutro">
                  {lista.data.romaneios.length}{' '}
                  {lista.data.romaneios.length === 1 ? 'romaneio' : 'romaneios'}
                </Pilula>
              ) : null}
            </div>

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoFraco" />
              <input
                value={busca}
                onChange={(e) => definirBusca(e.target.value)}
                placeholder="romaneio, televenda, nota fiscal ou transportadora"
                aria-label="Buscar romaneio"
                className="min-h-[38px] w-full rounded-lg border border-borda bg-superficie pl-9 pr-3 text-[13px]"
              />
            </div>
            {buscaAtrasada.trim() !== '' ? (
              <p className="text-[11.5px] text-textoFraco">
                Buscando em todo o histórico — o período acima só vale para a lista sem busca.
              </p>
            ) : null}

            {lista.isLoading ? (
              <Esqueleto className="h-64" />
            ) : lista.isError ? (
              <EstadoErro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
            ) : (lista.data?.romaneios.length ?? 0) === 0 ? (
              <>
                <EstadoVazio
                  icone={<Truck className="h-7 w-7" />}
                  titulo={
                    buscaAtrasada.trim()
                      ? `Nada com “${buscaAtrasada}” na empresa ${empresa ?? ''}`
                      : 'Nenhum romaneio no período'
                  }
                  detalhe={
                    buscaAtrasada.trim()
                      ? 'A lista cobre qualquer transportadora, mas só os romaneios que levam pedido desta empresa.'
                      : 'Amplie o período acima, ou digite o número do romaneio.'
                  }
                />
                <NoutraEmpresa achados={lista.data?.outrasEmpresas ?? []} />
              </>
            ) : (
              <ul className="divide-y divide-borda">
                {lista.data?.romaneios.map((r) => (
                  <LinhaLista
                    key={r.romaneio}
                    romaneio={r}
                    empresaAtual={empresa}
                    ativo={numeroSelecionado === r.romaneio}
                    aoAbrir={() => definirSelecionado(String(r.romaneio))}
                  />
                ))}
              </ul>
            )}
          </CorpoCartao>
        </Cartao>

        <div className="lg:min-h-0 lg:overflow-y-auto lg:pr-1">
        {numeroSelecionado === null ? (
          <Cartao className="grid min-h-[280px] place-items-center">
            <EstadoVazio
              icone={<FileSearch className="h-7 w-7" />}
              titulo="Escolha um romaneio"
              detalhe="Clique num romaneio da lista, ou digite o número no campo acima."
            />
          </Cartao>
        ) : detalhe.isLoading ? (
          <Esqueleto className="h-[420px]" />
        ) : detalhe.isError ? (
          <Cartao>
            <CorpoCartao>
              <EstadoErro
                erro={detalhe.error}
                aoTentarDeNovo={() => void detalhe.refetch()}
              />
              {detalhe.error instanceof ErroHttp && detalhe.error.status === 404 ? (
                <p className="mt-2 text-[12.5px] text-textoFraco">
                  Se o número está certo, o romaneio pode ser de outra empresa — troque a empresa no
                  seletor do topo.
                </p>
              ) : null}
            </CorpoCartao>
          </Cartao>
        ) : detalhe.data ? (
          <Detalhe detalhe={detalhe.data} empresaAtual={empresa} />
        ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * "Não é aqui, é ali" — a saída do beco sem saída.
 *
 * Procurar uma transportadora e não achar nada é ambíguo: pode ser que ela
 * não exista, pode ser que o nome esteja escrito diferente, ou — o caso
 * comum — pode ser que ela rode para OUTRA empresa do grupo. A Brasil Web é
 * exatamente isso: 714 romaneios em 60 dias, nenhum deles da empresa 32.
 *
 * Só aparecem empresas que este usuário já poderia abrir, e o botão leva ao
 * centro DELE naquela empresa — a lista de centros do próprio contexto é que
 * decide qual, então não há como oferecer uma porta que dá 403.
 */
function NoutraEmpresa({ achados }: { achados: RomaneiosEmOutraEmpresa[] }): JSX.Element | null {
  const { centros, definirCentro } = useContexto();
  if (achados.length === 0) return null;

  // Um centro que o usuário realmente tem naquela empresa, ou nada.
  const centroDe = (empresa: number): number | null =>
    centros.find((c) => c.empresa === empresa)?.centroEstoque ?? null;

  const comPorta = achados.filter((a) => centroDe(a.empresa) !== null);
  if (comPorta.length === 0) return null;

  return (
    <div className="rounded-xl border border-borda bg-superficie2 p-3">
      <p className="flex items-start gap-2 text-[12.5px] font-semibold">
        <AlertTriangle className="mt-[1px] h-4 w-4 shrink-0 text-atencao" />
        Essa busca acha romaneio em outra empresa.
      </p>
      <ul className="mt-2 space-y-1.5">
        {comPorta.map((a) => (
          <li key={a.empresa} className="flex flex-wrap items-center gap-2">
            <span className="text-[12.5px]">
              <strong className="tabular">{a.empresa}</strong>
              {a.nome ? ` · ${a.nome}` : ''} —{' '}
              <span className="tabular">{numero(a.romaneios)}</span>{' '}
              {a.romaneios === 1 ? 'romaneio' : 'romaneios'} em 90 dias
              {a.ultimo ? `, o último em ${dataHora(a.ultimo)}` : ''}
            </span>
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => definirCentro(centroDe(a.empresa) as number)}
            >
              Abrir nessa empresa
            </Botao>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * O campo de número — o caminho mais curto até um romaneio.
 *
 * Fica no cabeçalho, e não dentro da lista, porque não é um filtro: é uma
 * ida direta. Quem reimprime tem o número na mão.
 */
function AbrirPorNumero({ aoAbrir }: { aoAbrir: (n: number) => void }): JSX.Element {
  const [valor, definirValor] = React.useState('');
  return (
    <form
      className="flex items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        const n = Number(valor.replace(/\D/g, ''));
        if (n > 0) {
          aoAbrir(n);
          definirValor('');
        }
      }}
    >
      <label htmlFor="num-romaneio" className="rotulo">
        Romaneio nº
      </label>
      <input
        id="num-romaneio"
        value={valor}
        onChange={(e) => definirValor(e.target.value.replace(/\D/g, ''))}
        inputMode="numeric"
        placeholder="000000"
        className="tabular min-h-[34px] w-[110px] rounded-lg border border-borda bg-superficie px-2 text-center font-mono text-[14px] font-bold"
      />
      <Botao variante="secundaria" tamanho="pequeno" type="submit" disabled={valor === ''}>
        Abrir
      </Botao>
    </form>
  );
}

function LinhaLista({
  romaneio,
  empresaAtual,
  ativo,
  aoAbrir,
}: {
  romaneio: RomaneioResumo;
  empresaAtual: number | null;
  ativo: boolean;
  aoAbrir: () => void;
}): JSX.Element {
  const misto = romaneio.empresas.length > 1;
  return (
    <li>
      <button
        type="button"
        onClick={aoAbrir}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors',
          ativo ? 'bg-primaria-suave' : 'hover:bg-superficie2',
        )}
      >
        <span
          className={cn(
            'tabular w-[74px] shrink-0 font-mono text-[14px] font-bold',
            ativo && 'text-primaria',
          )}
        >
          {romaneio.romaneio}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12.5px] font-semibold">
            {romaneio.transportadoraNome ?? 'sem transportadora'}
          </span>
          <span className="tabular block text-[11px] text-textoFraco">
            {dataHora(romaneio.data)} · {numero(romaneio.pedidos)}{' '}
            {romaneio.pedidos === 1 ? 'pedido' : 'pedidos'} · {numero(romaneio.volumes)} vol
          </span>
        </span>
        {misto ? (
          <Pilula tom="alerta" title={`Empresas ${romaneio.empresas.join(', ')}`}>
            misto
          </Pilula>
        ) : romaneio.empresas[0] !== undefined && romaneio.empresas[0] !== empresaAtual ? (
          <Pilula tom="neutro">emp {romaneio.empresas[0]}</Pilula>
        ) : null}
      </button>
    </li>
  );
}

function Detalhe({
  detalhe,
  empresaAtual,
}: {
  detalhe: RomaneioDetalhe;
  empresaAtual: number | null;
}): JSX.Element {
  const { cabecalho, linhas } = detalhe;
  const [verPapel, definirVerPapel] = React.useState(false);
  const [abrindoA4, definirAbrindoA4] = React.useState(false);
  const [erroA4, definirErroA4] = React.useState<string | null>(null);

  // O A4 sai pelo Edge, com prévia e escolha de impressora (ver abrirRomaneioNoEdge).
  async function imprimirA4(): Promise<void> {
    definirErroA4(null);
    definirAbrindoA4(true);
    try {
      await abrirRomaneioNoEdge(cabecalho.romaneio);
    } catch (e) {
      definirErroA4(e instanceof ErroHttp ? e.message : 'Não foi possível abrir o romaneio.');
    } finally {
      definirAbrindoA4(false);
    }
  }
  const { temPermissao } = useSessao();
  const podeRetirar = temPermissao('romaneio.retirar');
  const retirada = useRetirada(cabecalho.romaneio);
  const exclusao = useExclusaoRomaneio(cabecalho.romaneio);

  const outras = cabecalho.empresas.filter((e) => e !== empresaAtual);
  const deOutras = linhas.filter((l) => l.empresa !== null && l.empresa !== empresaAtual).length;

  return (
    <div className="space-y-3">
      <Cartao>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-borda px-4 py-3">
          <div className="min-w-0">
            <p className="rotulo">Romaneio de Expedição</p>
            <h2 className="tabular font-mono text-[24px] font-bold leading-tight">
              {cabecalho.romaneio}
            </h2>
            <p className="mt-0.5 text-[12.5px] text-textoFraco">
              {dataHora(cabecalho.data)}
              {cabecalho.cracha !== null ? ` · montado pelo crachá ${cabecalho.cracha}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => definirVerPapel((v) => !v)}
            >
              {verPapel ? 'Esconder o papel' : 'Ver o papel'}
            </Botao>
            <Botao
              variante="primaria"
              tamanho="pequeno"
              onClick={() => void imprimirA4()}
              carregando={abrindoA4}
              disabled={linhas.length === 0}
              title="Abre o romaneio no Edge, com a prévia e a escolha da impressora A4."
            >
              <Printer className="h-4 w-4" />
              Imprimir Romaneio
            </Botao>
            {podeRetirar ? (
              exclusao.confirmando ? (
                <>
                  <Botao variante="fantasma" tamanho="pequeno" onClick={exclusao.cancelar} disabled={exclusao.excluindo}>
                    Cancelar
                  </Botao>
                  <Botao
                    variante="perigo"
                    tamanho="pequeno"
                    carregando={exclusao.excluindo}
                    onClick={() => void exclusao.excluir()}
                    title="Apaga o romaneio no ERP (regra de exclusão do Procfit) e volta os pedidos para Faturado."
                  >
                    <Trash2 className="h-4 w-4" />
                    Confirmar exclusão de {numero(detalhe.qtdePedidos)} pedido(s)
                  </Botao>
                </>
              ) : (
                <Botao variante="fantasma" tamanho="pequeno" onClick={exclusao.pedir} title="Excluir o romaneio inteiro">
                  <Trash2 className="h-4 w-4" />
                  Excluir romaneio
                </Botao>
              )
            ) : null}
          </div>
        </div>

        <CorpoCartao className="space-y-3">
          {exclusao.erro ? (
            <p className="nao-imprimir flex items-start gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {exclusao.erro}
            </p>
          ) : null}
          {erroA4 ? (
            <p className="nao-imprimir flex items-start gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {erroA4}
            </p>
          ) : null}
          <EtiquetasDoMarketplace romaneio={cabecalho.romaneio} />

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
            <Dado rotulo="Transportadora" valor={cabecalho.transportadoraNome ?? '—'} forte />
            <Dado rotulo="Pedidos" valor={numero(detalhe.qtdePedidos)} />
            <Dado rotulo="Volumes" valor={numero(detalhe.totalVolumes)} />
            <Dado rotulo="Peso" valor={`${numero(detalhe.totalPesoGramas)} g`} />
          </div>

          {cabecalho.observacao ? (
            <p className="rounded-lg bg-superficie2 px-3 py-2 text-[12.5px]">
              {cabecalho.observacao}
            </p>
          ) : null}

          {outras.length > 0 ? (
            <p className="flex items-start gap-2 rounded-lg bg-status-atencaoFundo px-3 py-2 text-[12.5px] text-status-atencao">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>
                  {numero(deOutras)} {deOutras === 1 ? 'pedido' : 'pedidos'} deste romaneio{' '}
                  {deOutras === 1 ? 'é' : 'são'} da empresa {outras.join(' e ')}
                </strong>
                , não da {empresaAtual}. Isso é normal: o coletor monta o romaneio por
                TRANSPORTADORA, então a mesma viagem pode levar caixa de mais de uma empresa. As
                linhas de fora estão marcadas na tabela abaixo. O romaneio impresso sai com todas —
                é uma carga só, e é o que o Procfit também imprime.
              </span>
            </p>
          ) : null}

          {/* Pedidos contam pelo que FOI IMPRESSO; volumes são os bipados na
              montagem (o total do ERP). Quando uma nota é cancelada, a linha
              some do papel mas o ERP continua contando o pedido. */}
          {cabecalho.pedidos !== detalhe.qtdePedidos ? (
            <p className="text-[11.5px] text-textoFraco">
              O ERP registra {numero(cabecalho.pedidos)} pedidos neste romaneio; o papel imprime{' '}
              {numero(detalhe.qtdePedidos)}. A diferença costuma ser nota cancelada depois da
              montagem — o total de volumes ainda conta as caixas desses pedidos.
            </p>
          ) : null}

          {linhas.length === 0 ? (
            <EstadoVazio
              titulo="Sem linhas para imprimir"
              detalhe="Todos os pedidos deste romaneio estão com a nota cancelada, ou ainda sem nota."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="border-b border-borda text-left">
                    <th className="rotulo py-1.5 pr-2">Televenda</th>
                    <th className="rotulo py-1.5 pr-2">Objeto</th>
                    <th className="rotulo py-1.5 pr-2">Destino</th>
                    <th className="rotulo py-1.5 pr-2 text-right">Peso</th>
                    <th className="rotulo py-1.5 pr-2 text-center">Vol</th>
                    <th className="rotulo py-1.5 pr-2 text-center">NF</th>
                    <th className="rotulo py-1.5">Serviço</th>
                    {podeRetirar ? (
                      <th className="rotulo nao-imprimir py-1.5 pl-2 text-right">Ação</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l, i) => {
                    const deOutraEmpresa = l.empresa !== null && l.empresa !== empresaAtual;
                    return (
                    <tr
                      key={`${l.televenda}-${l.notaFiscal ?? i}`}
                      className={cn(
                        'border-b border-borda/60',
                        deOutraEmpresa && 'bg-status-atencaoFundo',
                      )}
                    >
                      <td className="tabular py-1.5 pr-2 font-mono font-semibold">
                        {l.televenda}
                        {deOutraEmpresa ? (
                          <span
                            className="tabular ml-1.5 rounded bg-status-atencao/15 px-1 text-[10px] font-bold text-status-atencao"
                            title={`Pedido da empresa ${l.empresa}, não da ${empresaAtual}`}
                          >
                            emp {l.empresa}
                          </span>
                        ) : null}
                      </td>
                      <td
                        className="max-w-[150px] truncate py-1.5 pr-2 font-mono text-[11.5px] text-textoFraco"
                        title={l.objeto ?? undefined}
                      >
                        {l.objeto ?? '—'}
                      </td>
                      <td className="py-1.5 pr-2">
                        <span className="font-semibold">{l.cidade ?? '—'}</span>
                        <span className="ml-1 text-textoFraco">{l.uf ?? ''}</span>
                        <span className="tabular ml-1 text-[11px] text-textoSuave">{l.cep}</span>
                      </td>
                      <td className="tabular py-1.5 pr-2 text-right">{numero(l.pesoGramas)} g</td>
                      <td className="tabular py-1.5 pr-2 text-center">{l.volumes}</td>
                      <td className="tabular py-1.5 pr-2 text-center">{l.notaFiscal ?? '—'}</td>
                      <td className="py-1.5 text-[11.5px]">{l.tipoServico ?? '—'}</td>
                      {podeRetirar ? (
                        <td className="nao-imprimir py-1.5 pl-2 text-right">
                          <BotaoRetirar
                            televenda={l.televendaNumero}
                            notaValida
                            retirada={retirada}
                          />
                        </td>
                      ) : null}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {retirada.erro ? (
            <p className="nao-imprimir rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
              {retirada.erro}
            </p>
          ) : null}
          {retirada.retirado !== null ? (
            <p className="nao-imprimir rounded-lg bg-status-atencaoFundo px-3 py-2 text-[12.5px] text-status-atencao">
              Pedido {retirada.retirado} retirado e de volta a “Faturado”. Ele está fora de qualquer
              romaneio: bipe de novo pelo coletor num romaneio para ele seguir viagem e ter
              etiqueta.
            </p>
          ) : null}

          <PedidosPresos
            comandas={detalhe.comandas}
            retirada={retirada}
            podeRetirar={podeRetirar}
          />

          {podeRetirar ? (
            <p className="nao-imprimir text-[11.5px] text-textoFraco">
              Retirar tira a comanda do romaneio, registra a saída no ERP e volta o pedido de “Em
              Romaneio” para “Faturado”. A nota e o estoque não são tocados — e não se desfaz por
              aqui. O pedido fica fora de qualquer romaneio (e sem etiqueta na tela de etiquetas)
              até ser bipado de novo pelo coletor.
            </p>
          ) : (
            <p className="nao-imprimir text-[11.5px] text-textoFraco">
              Você não tem a permissão <code>romaneio.retirar</code> para tirar pedido da carga.
            </p>
          )}
        </CorpoCartao>
      </Cartao>

      {/* A prévia do papel, quando pedida. A impressão sai da página `/romaneio-a4`, no Edge. */}
      {verPapel ? (
        <div className="cartao overflow-x-auto p-4">
          <style>{CSS_ROMANEIO}</style>
          <p className="rotulo mb-2">Prévia do papel (A4)</p>
          <div style={{ width: '210mm', maxWidth: '100%' }}>
            <RomaneioPapel
              romaneio={cabecalho.romaneio}
              data={cabecalho.data}
              transportadora={cabecalho.transportadoraNome}
              linhas={linhas}
              qtdePedidos={detalhe.qtdePedidos}
              totalVolumes={detalhe.totalVolumes}
              totalPesoGramas={detalhe.totalPesoGramas}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Excluir o romaneio inteiro, em dois toques (o segundo diz quantos pedidos
 * saem). A API roda a regra de exclusão do dicionário do PBS e volta os
 * pedidos de "Em Romaneio" para "Faturado". Depois, volta para a lista.
 */
function useExclusaoRomaneio(romaneio: number): {
  confirmando: boolean;
  excluindo: boolean;
  erro: string | null;
  pedir: () => void;
  cancelar: () => void;
  excluir: () => Promise<void>;
} {
  const cliente = useQueryClient();
  const [confirmando, definirConfirmando] = React.useState(false);
  const [excluindo, definirExcluindo] = React.useState(false);
  const [erro, definirErro] = React.useState<string | null>(null);
  const [, definirSelecionado] = useParamUrl('romaneio');
  React.useEffect(() => {
    definirConfirmando(false);
    definirErro(null);
  }, [romaneio]);
  const excluir = React.useCallback(async () => {
    definirExcluindo(true);
    definirErro(null);
    try {
      await api.del(`/api/romaneios/${romaneio}`);
      definirConfirmando(false);
      await cliente.invalidateQueries({ queryKey: ['romaneios'] });
      definirSelecionado(null, { substituir: true });
    } catch (e) {
      definirErro(e instanceof Error ? e.message : 'Não foi possível excluir o romaneio.');
    } finally {
      definirExcluindo(false);
    }
  }, [romaneio, cliente]);
  return {
    confirmando,
    excluindo,
    erro,
    pedir: () => definirConfirmando(true),
    cancelar: () => definirConfirmando(false),
    excluir,
  };
}

/**
 * O mecanismo de retirar um pedido da carga, compartilhado pelos dois lugares
 * em que ele aparece: a tabela do papel e o bloco dos pedidos sem nota válida.
 *
 * Um estado só para os dois — `confirmando` guarda a televenda aguardando o
 * segundo toque. Assim nunca há duas confirmações abertas ao mesmo tempo, que
 * é o jeito de alguém confirmar a linha errada.
 */
function useRetirada(romaneio: number): {
  confirmando: number | null;
  retirando: number | null;
  erro: string | null;
  /** O último pedido retirado com sucesso — para o aviso de que ficou sem romaneio. */
  retirado: number | null;
  pedir: (televenda: number) => void;
  desistir: () => void;
  retirar: (televenda: number) => Promise<void>;
} {
  const cliente = useQueryClient();
  const [confirmando, definirConfirmando] = React.useState<number | null>(null);
  const [retirando, definirRetirando] = React.useState<number | null>(null);
  const [erro, definirErro] = React.useState<string | null>(null);
  const [retirado, definirRetirado] = React.useState<number | null>(null);

  const retirar = React.useCallback(
    async (televenda: number): Promise<void> => {
      definirRetirando(televenda);
      definirErro(null);
      try {
        await api.del(`/api/romaneios/${romaneio}/televendas/${televenda}`);
        definirRetirado(televenda);
        await cliente.invalidateQueries({ queryKey: ['romaneio', romaneio] });
        await cliente.invalidateQueries({ queryKey: ['romaneios'] });
      } catch (e) {
        definirErro(e instanceof Error ? e.message : 'Não foi possível retirar o pedido.');
      } finally {
        definirRetirando(null);
        definirConfirmando(null);
      }
    },
    [romaneio, cliente],
  );

  return {
    confirmando,
    retirando,
    erro,
    retirado,
    pedir: (televenda) => {
      definirErro(null);
      definirRetirado(null);
      definirConfirmando(televenda);
    },
    desistir: () => definirConfirmando(null),
    retirar,
  };
}

/**
 * Retirar, em dois toques e no lugar — sem modal.
 *
 * O segundo toque muda de rótulo conforme o que está em jogo: pedido com nota
 * válida sai da carga de uma viagem que já foi montada, e quem clica precisa
 * ver isso antes de confirmar, não depois.
 */
function BotaoRetirar({
  televenda,
  notaValida,
  retirada,
}: {
  televenda: number;
  notaValida: boolean;
  retirada: ReturnType<typeof useRetirada>;
}): JSX.Element {
  if (retirada.confirmando === televenda) {
    return (
      <span
        className="flex items-center justify-end gap-1.5"
        title="O pedido fica fora de qualquer romaneio até ser bipado de novo pelo coletor."
      >
        <span className="text-[11px] text-status-atencao">fica sem romaneio</span>
        <Botao
          variante="perigo"
          tamanho="micro"
          onClick={() => void retirada.retirar(televenda)}
          carregando={retirada.retirando === televenda}
        >
          {notaValida ? 'Tirar da carga' : 'Confirmar'}
        </Botao>
        <Botao
          variante="fantasma"
          tamanho="micro"
          onClick={retirada.desistir}
          disabled={retirada.retirando !== null}
        >
          Não
        </Botao>
      </span>
    );
  }

  return (
    <Botao
      variante="fantasma"
      tamanho="micro"
      onClick={() => retirada.pedir(televenda)}
      disabled={retirada.retirando !== null}
      aria-label={`Retirar o pedido ${televenda} do romaneio`}
    >
      <PackageX className="h-3.5 w-3.5" />
      Retirar
    </Botao>
  );
}

/**
 * Os pedidos que ficaram presos na carga.
 *
 * O coletor bipou a caixa, e DEPOIS a nota foi cancelada. A linha sai do papel
 * sozinha — o relatório corta nota cancelada — mas a comanda continua gravada,
 * e o cabeçalho do ERP segue contando aquele pedido e aqueles volumes. É a
 * diferença que o aviso acima já denuncia ("o ERP registra X pedidos... o papel
 * imprime Y"); aqui ela ganha nome, número e o botão que resolve.
 *
 * O bloco só existe quando há o que resolver. Romaneio limpo não mostra nada.
 *
 * Confirmação em dois toques, no lugar, sem modal: retirar não se desfaz por
 * aqui — quem quiser o pedido de volta na carga tem que passar pelo Procfit.
 */
function PedidosPresos({
  comandas,
  retirada,
  podeRetirar,
}: {
  comandas: ComandaRomaneio[];
  retirada: ReturnType<typeof useRetirada>;
  podeRetirar: boolean;
}): JSX.Element | null {
  const presos = comandas.filter((c) => c.situacao !== 'faturada');
  if (presos.length === 0) return null;

  const um = presos.length === 1;

  return (
    <div className="nao-imprimir space-y-2 rounded-xl border border-borda bg-superficie2 px-3 py-2.5">
      <p className="flex items-start gap-2 text-[12.5px]">
        <PackageX className="mt-0.5 h-4 w-4 shrink-0 text-status-divergencia" />
        <span>
          <strong>
            {numero(presos.length)} {um ? 'pedido' : 'pedidos'} sem nota válida
          </strong>{' '}
          {um ? 'continua' : 'continuam'} nesta carga. Não {um ? 'sai' : 'saem'} no papel, mas{' '}
          {um ? 'conta' : 'contam'} no total do ERP.
        </span>
      </p>

      <ul className="divide-y divide-borda">
        {presos.map((c) => (
          <li key={c.televenda} className="flex flex-wrap items-center gap-2 py-1.5">
            <span className="tabular w-[86px] shrink-0 font-mono text-[13px] font-bold">
              {c.televenda}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[12px]">
                {c.situacao === 'cancelada' ? (
                  <>
                    Nota <strong className="tabular font-mono">{c.notaFiscal ?? '—'}</strong>{' '}
                    cancelada em {dataHora(c.canceladaEm)}
                  </>
                ) : (
                  'Sem nota emitida'
                )}
              </span>
              <span className="block text-[11px] text-textoFraco">
                {c.cidade ?? '—'} {c.uf ?? ''}
                {c.empresa === null ? '' : ` · emp ${c.empresa}`}
              </span>
            </span>
            {podeRetirar ? (
              <BotaoRetirar televenda={c.televenda} notaValida={false} retirada={retirada} />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * O atalho para as etiquetas do marketplace, quando o romaneio tem alguma.
 *
 * O par natural de "imprimir o romaneio" é "imprimir as etiquetas": os dois
 * papéis saem no mesmo momento, um vai na mão do motorista e o outro na caixa.
 * Separá-los em telas diferentes obrigava a trocar de tela e digitar o mesmo
 * número de novo.
 *
 * O bloco só aparece quando há pedido de marketplace — a consulta é só banco
 * (contagem), sem ida ao Mercado Livre, para não custar nada nos romaneios que
 * não têm nenhum, que são a maioria.
 */
function EtiquetasDoMarketplace({ romaneio }: { romaneio: number }): JSX.Element | null {
  const [baixando, definirBaixando] = React.useState<'pdf' | 'zpl' | null>(null);
  const [erro, definirErro] = React.useState<string | null>(null);
  const [aviso, definirAviso] = React.useState<string | null>(null);

  interface ResumoMkt {
    romaneio: number;
    pedidos: number;
    comEnvio: number;
    cancelados: number;
    aguardandoNota: number;
    retidos: number;
  }

  const resumo = useQuery<ResumoMkt>({
    queryKey: ['etiquetas', 'resumo', romaneio],
    queryFn: () => api.get<ResumoMkt>(`/api/etiquetas/romaneio/${romaneio}/resumo`),
    staleTime: 60_000,
    retry: false,
  });

  if (!resumo.data || resumo.data.pedidos === 0) return null;
  const { pedidos, cancelados, aguardandoNota, retidos } = resumo.data;

  /*
   * As pendências, conforme a última foto do banco.
   *
   * Esta tela não consulta o Mercado Livre — quem quer o número confirmado
   * abre Etiquetas. Mas mostrar o aviso aqui é o que impede alguém de mandar
   * imprimir a carga inteira sem saber que faltam etiquetas, que é exatamente
   * o momento em que o chamado nasce. Por isso o texto diz de onde vem o
   * número em vez de apresentá-lo como certeza.
   */
  const motivos = [
    cancelados > 0 ? `${numero(cancelados)} cancelado${cancelados === 1 ? '' : 's'}` : null,
    aguardandoNota > 0 ? `${numero(aguardandoNota)} aguardando nota fiscal` : null,
    retidos > 0 ? `${numero(retidos)} retido${retidos === 1 ? '' : 's'} pelo ML` : null,
  ].filter((m): m is string => m !== null);

  async function baixar(formato: 'pdf' | 'zpl'): Promise<void> {
    definirBaixando(formato);
    definirErro(null);
    definirAviso(null);
    try {
      const resposta = await fetch(`/api/etiquetas/romaneio/${romaneio}/pdf?formato=${formato}`, {
        credentials: 'include',
      });

      if (!resposta.ok) {
        const corpo = (await resposta.json().catch(() => null)) as { mensagem?: string } | null;
        definirErro(corpo?.mensagem ?? 'Não foi possível gerar as etiquetas.');
        return;
      }

      const liberados = Number(resposta.headers.get('X-Xml-Liberados') ?? 0);
      if (liberados > 0) {
        definirAviso(
          `${liberados} ${liberados === 1 ? 'etiqueta estava presa' : 'etiquetas estavam presas'} esperando a nota fiscal. O XML foi enviado ao Mercado Livre e ${liberados === 1 ? 'ela entrou' : 'elas entraram'} na impressão.`,
        );
      }

      const url = URL.createObjectURL(await resposta.blob());
      if (formato === 'pdf') {
        window.open(url, '_blank', 'noopener');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `etiquetas-romaneio-${romaneio}.zip`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      definirErro('Não foi possível falar com o servidor.');
    } finally {
      definirBaixando(null);
    }
  }

  return (
    <div className="nao-imprimir space-y-2 rounded-xl border border-borda bg-superficie2 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Tags className="h-4 w-4 shrink-0 text-textoFraco" />
        <span className="text-[12.5px]">
          <strong>
            {numero(pedidos)} {pedidos === 1 ? 'pedido' : 'pedidos'} de marketplace
          </strong>{' '}
          {pedidos === 1 ? 'está' : 'estão'} nesta carga.
        </span>

        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          <Botao
            variante="secundaria"
            tamanho="pequeno"
            onClick={() => void baixar('pdf')}
            carregando={baixando === 'pdf'}
            disabled={baixando !== null}
          >
            <Printer className="h-4 w-4" />
            Etiquetas (PDF)
          </Botao>
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            onClick={() => void baixar('zpl')}
            carregando={baixando === 'zpl'}
            disabled={baixando !== null}
            title="Baixa as etiquetas em ZPL (.zip), para impressora Zebra."
          >
            ZPL
          </Botao>
          <Link
            to={`/etiquetas?romaneio=${romaneio}`}
            className={cn(acaoTexto, 'text-[12px]')}
          >
            ver situação de cada pedido
          </Link>
        </span>
      </div>

      {motivos.length > 0 ? (
        <p className="flex items-start gap-2 rounded-lg border border-status-divergencia/50 bg-status-divergenciaFundo px-2.5 py-2 text-[12px] text-status-divergencia">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Este romaneio tem pendência de etiqueta:</strong> {motivos.join(', ')}. Nem
            todas as etiquetas vão sair. Conforme a última foto do banco — abra{' '}
            <Link
              to={`/etiquetas?romaneio=${romaneio}`}
              className="font-semibold underline underline-offset-2"
            >
              Etiquetas
            </Link>{' '}
            para confirmar no Mercado Livre e ver o motivo pedido a pedido.
          </span>
        </p>
      ) : null}

      {erro ? (
        <p className="rounded-lg bg-status-divergenciaFundo px-2.5 py-1.5 text-[12px] text-status-divergencia">
          {erro}
        </p>
      ) : null}
      {aviso ? (
        <p className="rounded-lg bg-status-livreFundo px-2.5 py-1.5 text-[12px] text-status-livre">
          {aviso}
        </p>
      ) : null}
    </div>
  );
}

function Dado({
  rotulo,
  valor,
  forte,
}: {
  rotulo: string;
  valor: string;
  forte?: boolean;
}): JSX.Element {
  return (
    <span className="min-w-0">
      <span className="rotulo block">{rotulo}</span>
      <span
        className={cn(
          'block truncate',
          forte ? 'text-[13.5px] font-bold' : 'tabular text-[13.5px] font-semibold',
        )}
      >
        {valor}
      </span>
    </span>
  );
}
